import { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, Clock, Send, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import Reveal from "../components/Reveal";
import PageHeader from "../components/PageHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", company: "", country: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, source: "contact" });
      toast.success("Message received. We will respond within 24 business hours.");
      setForm({ name: "", company: "", country: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Could not submit. Please email info@deeprootsspice.com directly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Get In Touch"
        title="Let's begin a conversation."
        italicWord="conversation"
        subtitle="Whether it's a first sample request or a container-scale enquiry, our export team responds to every message within one business day."
      />

      <section className="py-24 md:py-32 bg-brand-ivory">
        <div className="container-luxe grid lg:grid-cols-12 gap-14">
          {/* Details */}
          <Reveal className="lg:col-span-5">
            <span className="overline">Trade Enquiries</span>
            <h2 className="font-serif text-3xl md:text-4xl mt-4 text-brand-charcoal">Speak with our export team.</h2>
            <span className="divider-gold" />

            <ul className="space-y-8 mt-8">
              <li className="flex items-start gap-5">
                <div className="w-11 h-11 border border-brand-gold flex items-center justify-center text-brand-gold shrink-0">
                  <Phone className="w-4 h-4" />
                </div>
                <div>
                  <div className="overline text-brand-charcoal/50">Phone / WhatsApp</div>
                  <a href="tel:+919211974059" className="mt-2 block font-serif text-2xl text-brand-charcoal hover:text-brand-green transition-colors" data-testid="contact-phone">+91 9211974059</a>
                  <div className="text-[13px] text-brand-charcoal/50 mt-1">Nidhi Goud — Export Sales</div>
                </div>
              </li>
              <li className="flex items-start gap-5">
                <div className="w-11 h-11 border border-brand-gold flex items-center justify-center text-brand-gold shrink-0">
                  <Mail className="w-4 h-4" />
                </div>
                <div>
                  <div className="overline text-brand-charcoal/50">Email</div>
                  <a href="mailto:info@deeprootsspice.com" className="mt-2 block font-serif text-2xl text-brand-charcoal hover:text-brand-green transition-colors" data-testid="contact-email">info@deeprootsspice.com</a>
                  <div className="text-[13px] text-brand-charcoal/50 mt-1">Responses within 24 business hours</div>
                </div>
              </li>
              <li className="flex items-start gap-5">
                <div className="w-11 h-11 border border-brand-gold flex items-center justify-center text-brand-gold shrink-0">
                  <MapPin className="w-4 h-4" />
                </div>
                <div>
                  <div className="overline text-brand-charcoal/50">Export Office</div>
                  <div className="mt-2 font-serif text-2xl text-brand-charcoal">India</div>
                  <div className="text-[13px] text-brand-charcoal/50 mt-1">Full address provided on serious enquiry</div>
                </div>
              </li>
              <li className="flex items-start gap-5">
                <div className="w-11 h-11 border border-brand-gold flex items-center justify-center text-brand-gold shrink-0">
                  <Clock className="w-4 h-4" />
                </div>
                <div>
                  <div className="overline text-brand-charcoal/50">Business Hours</div>
                  <div className="mt-2 font-serif text-xl text-brand-charcoal">Mon – Sat · 09:30 – 19:00 IST</div>
                  <div className="text-[13px] text-brand-charcoal/50 mt-1">WhatsApp available beyond office hours</div>
                </div>
              </li>
            </ul>

            <a href="https://wa.me/919211974059" target="_blank" rel="noopener noreferrer" className="mt-10 inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-brand-green font-semibold border-b border-brand-green pb-1" data-testid="contact-whatsapp-link">
              <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
            </a>
          </Reveal>

          {/* Form */}
          <Reveal delay={150} className="lg:col-span-6 lg:col-start-7">
            <form onSubmit={submit} className="p-10 bg-brand-ivory-warm border border-brand-charcoal/10" data-testid="contact-form">
              <span className="overline">Send us a message</span>
              <h3 className="font-serif text-3xl mt-3 mb-8 text-brand-charcoal">General Enquiry</h3>
              <div className="grid md:grid-cols-2 gap-x-6 gap-y-2">
                <div>
                  <label className="overline text-brand-charcoal/50 block mb-1">Full Name*</label>
                  <input required value={form.name} onChange={set("name")} className="input-editorial" data-testid="contact-name" />
                </div>
                <div>
                  <label className="overline text-brand-charcoal/50 block mb-1">Company</label>
                  <input value={form.company} onChange={set("company")} className="input-editorial" data-testid="contact-company" />
                </div>
                <div>
                  <label className="overline text-brand-charcoal/50 block mb-1">Email*</label>
                  <input required type="email" value={form.email} onChange={set("email")} className="input-editorial" data-testid="contact-email-input" />
                </div>
                <div>
                  <label className="overline text-brand-charcoal/50 block mb-1">Phone</label>
                  <input value={form.phone} onChange={set("phone")} className="input-editorial" data-testid="contact-phone-input" />
                </div>
                <div className="md:col-span-2">
                  <label className="overline text-brand-charcoal/50 block mb-1">Country</label>
                  <input value={form.country} onChange={set("country")} className="input-editorial" data-testid="contact-country" />
                </div>
                <div className="md:col-span-2">
                  <label className="overline text-brand-charcoal/50 block mb-1">Message</label>
                  <textarea rows={4} value={form.message} onChange={set("message")} className="input-editorial resize-none" data-testid="contact-message" />
                </div>
              </div>
              <button type="submit" disabled={busy} className="mt-8 luxe-btn-primary disabled:opacity-60" data-testid="contact-submit">
                {busy ? "Sending..." : "Send Message"} <Send className="w-3.5 h-3.5" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      {/* MAP PLACEHOLDER */}
      <section className="pb-24 bg-brand-ivory">
        <div className="container-luxe">
          <div className="aspect-[16/6] bg-brand-ink relative overflow-hidden border border-brand-charcoal/10">
            <div className="absolute inset-0 opacity-40">
              <img src="/images/hero/hero-export.png" alt="" className="w-full h-full object-cover" />
            </div>
            <div className="absolute inset-0 bg-brand-ink/50 flex items-center justify-center text-brand-ivory text-center px-6">
              <div>
                <MapPin className="w-8 h-8 text-brand-gold mx-auto mb-4" />
                <div className="font-serif text-3xl">Export Office · India</div>
                <div className="text-[13px] tracking-[0.24em] uppercase text-brand-ivory/50 mt-3">Detailed location shared upon buyer verification</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
